// Styles
import 'react-circular-progressbar/dist/styles.css';

// React and Router
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Chakra UI
import { Box, Button, Flex, Spinner, Stack, Tabs, Text } from '@chakra-ui/react';

// Phosphor Icons
import { CaretLeft, CaretRight, Envelope, LinkedinLogo, UserCircle } from '@phosphor-icons/react';

// API
import { fetchAPI } from '@/api/fetch';
import {
  apiGetJobCandidateDetails,
  apiGetJobDetails,
  apiGetPreviousAndNextJobCandidates,
  apiUpdateJobCandidateFollowupQuestions,
  apiUpdateJobCandidateStatus,
  apiAddCandidateToJob,
  GetJobDetailsResponse,
  GetPreviousAndNextJobCandidatesResponse,
  UpdateJobCandidateStatusResponse
} from '@/api/services';

// Types
import { AlertsAndFollowUpQuestion } from '@/types/AnalysisResult';
import { CandidateWithJobContext, CandidateWithJobContextBasicInfo } from '@/types/Candidate';
import { Job } from '@/types/Job';
import { MeetingSummary } from '@/types/Meeting';
import { Screening } from '@/types/Screening';
import {
  CandidateDetailsTab,
  InterviewAnalysisSection,
  JobCandidateStatus,
  getJobCandidateStatusLabel,
  getStatusList
} from '@/types/enums';

// UI Components
import { Tooltip } from '@/components/ui/tooltip';
import { Skeleton, SkeletonCircle, SkeletonText } from '@/components/ui/skeleton';
import CircleProgressBar from '@/components/data-display/CircularProgressBar';
import { Step } from '@/components/data-display/Step';

// Local Components
import { BreadcrumbNavigation } from './components/BreadcrumbNavigation';
import { CandidateCard } from './components/CandidateCard';
import CandidateNotes from './CandidateNotes';
import CandidateSummary from './CandidateSummary';
import EditCandidateDialog from '@/views/Candidate/EditCandidateDialog';
import AddCandidateToJobDialog from '@/views/Candidate/AddCandidateToJobDialog';
import InterviewAnalysisIndex, { InterviewAnalysisIndexRef } from './InterviewAnalysisSection';
import ResumeAnalysis, { ResumeAnalysisRef } from './ResumeAnalysis';
import ScoreDetailsDialog from './ScoreDetailsDialog';
import { getCandidateFullName } from '@/utils';

// Constants for action button colors
const ACTION_COLORS = {
  reject: { color: 'error.700', bgColor: 'error.300', hoverBgColor: 'error.700' },
  withdraw: { color: 'warning.700', bgColor: 'warning.300', hoverBgColor: 'warning.700' },
  back: { color: 'neutrals.700', bgColor: 'neutrals.300', hoverBgColor: 'neutrals.700' },
  forward: { color: 'success.700', bgColor: 'success.300', hoverBgColor: 'success.700' }
} as const;

const ACTION_ORDER: Array<'reject' | 'withdraw' | 'back' | 'forward'> = [
  'reject',
  'withdraw',
  'back',
  'forward'
];

// Base status transitions (without dynamic logic)
const BASE_STATUS_TRANSITIONS: Record<
  JobCandidateStatus,
  Record<string, JobCandidateStatus | null> | null
> = {
  [JobCandidateStatus.ResumeAnalyzed]: {
    back: null,
    forward: JobCandidateStatus.Shortlist,
    reject: JobCandidateStatus.Rejected,
    withdraw: JobCandidateStatus.Withdrawn
  },
  [JobCandidateStatus.Shortlist]: {
    back: JobCandidateStatus.ResumeAnalyzed,
    forward: JobCandidateStatus.Interview,
    reject: JobCandidateStatus.Rejected,
    withdraw: JobCandidateStatus.Withdrawn
  },
  [JobCandidateStatus.Interview]: null, // Dynamic based on meeting
  [JobCandidateStatus.InterviewCompleted]: {
    back: null,
    forward: JobCandidateStatus.Pass,
    reject: JobCandidateStatus.Rejected,
    withdraw: JobCandidateStatus.Withdrawn
  },
  [JobCandidateStatus.Pass]: {
    back: JobCandidateStatus.InterviewCompleted,
    forward: null,
    reject: JobCandidateStatus.Rejected,
    withdraw: JobCandidateStatus.Withdrawn
  },
  [JobCandidateStatus.Rejected]: null, // Dynamic based on previous status
  [JobCandidateStatus.New]: {
    back: null,
    forward: null,
    reject: null,
    withdraw: null
  },
  [JobCandidateStatus.Fatal]: {
    back: null,
    forward: null,
    reject: null,
    withdraw: null
  },
  [JobCandidateStatus.Withdrawn]: null
};

export default function CandidateDetails() {
  const { jobId, candidateId } = useParams<{
    jobId: string;
    candidateId: string;
  }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [candidate, setCandidate] = useState<CandidateWithJobContext | null>(null);
  const [tab, setTab] = useState<CandidateDetailsTab>(CandidateDetailsTab.Summary);
  const [interviewAnalysisSection, setInterviewAnalysisSection] =
    useState<InterviewAnalysisSection>(InterviewAnalysisSection.Summary);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [statusList, setStatusList] = useState<string[]>([]);
  const [statusIndex, setStatusIndex] = useState<number>(0);
  const [previousCandidate, setPreviousCandidate] =
    useState<CandidateWithJobContextBasicInfo | null>(null);
  const [nextCandidate, setNextCandidate] = useState<CandidateWithJobContextBasicInfo | null>(null);
  const [tooltipState, setTooltipState] = useState({ previous: false, next: false });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const interviewAnalysisRef = useRef<InterviewAnalysisIndexRef>(null);
  const resumeAnalysisRef = useRef<ResumeAnalysisRef>(null);

  const updateCandidateField = useCallback((fields: Partial<CandidateWithJobContext>) => {
    setCandidate((prevCandidate) => (prevCandidate ? { ...prevCandidate, ...fields } : null));
  }, []);

  const fetchDetails = useCallback(async () => {
    if (!jobId || !candidateId) {
      navigate(`/job${jobId ? `/details/${jobId}` : ''}`);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const [jobResponse, candidateResponse, otherCandidatesResponse] = await Promise.all([
        fetchAPI<GetJobDetailsResponse>({
          request: () => apiGetJobDetails({ jobId })
        }),
        fetchAPI<CandidateWithJobContext>({
          request: () => apiGetJobCandidateDetails({ jobId, candidateId })
        }),
        fetchAPI<GetPreviousAndNextJobCandidatesResponse>({
          request: () => apiGetPreviousAndNextJobCandidates({ jobId, candidateId })
        })
      ]);

      if (jobResponse.success) {
        setJob(jobResponse.data.job_info);
      } else {
        setError('Failed to fetch job details');
        console.error('Failed to fetch job details:', jobResponse.message);
      }

      if (candidateResponse.success) {
        const candidateData = candidateResponse.data;
        setCandidate(candidateData);
        const { status, index } = getStatusList(candidateData.status, candidateData.progress);
        setStatusList(status);
        setStatusIndex(index);
      } else {
        setError('Failed to fetch candidate details');
        console.error('Failed to fetch candidate details:', candidateResponse.message);
      }

      if (otherCandidatesResponse.success) {
        setPreviousCandidate(otherCandidatesResponse.data.previous_candidate);
        setNextCandidate(otherCandidatesResponse.data.next_candidate);
      } else {
        console.error('Failed to fetch other candidates:', otherCandidatesResponse.message);
      }
    } catch (error) {
      setError('An error occurred while fetching details');
      console.error('Error fetching details:', error);
      navigate('/job');
    } finally {
      setIsLoading(false);
    }
  }, [jobId, candidateId, navigate]);

  useEffect(() => {
    fetchDetails();
  }, [jobId, candidateId, navigate, fetchDetails]);

  const lastWorkExperience = useMemo(
    () => candidate?.resume_data?.work_experiences[0] || null,
    [candidate?.resume_data?.work_experiences]
  );

  const updateCandidateStatus = useCallback(
    async (status: JobCandidateStatus) => {
      if (!candidate || !jobId || !candidateId) return;

      setIsStatusUpdating(true);
      try {
        const response = await fetchAPI<UpdateJobCandidateStatusResponse>({
          request: () =>
            apiUpdateJobCandidateStatus({
              jobId,
              candidateId,
              payload: { status }
            })
        });

        if (response.success) {
          const { status, progress } = response.data;
          updateCandidateField({ status, progress });
          const { status: newStatus, index } = getStatusList(status, progress);
          setStatusList(newStatus);
          setStatusIndex(index);
        } else {
          console.error('Failed to update candidate status:', response.message);
        }
      } catch (error) {
        console.error('Error updating candidate status:', error);
      } finally {
        setIsStatusUpdating(false);
      }
    },
    [candidate, jobId, candidateId, updateCandidateField]
  );

  const getDecisionList = useMemo((): Array<{
    value: string;
    label: string;
    enabled: boolean;
    onClick: () => void;
    color: string;
    bgColor: string;
    hoverBgColor: string;
  }> => {
    if (!candidate) return [];

    // Get previous status for rejection options
    const previousStatus = candidate.progress.length > 1 ? candidate.progress.at(-2) : null;

    // Get available transitions for current status
    let availableTransitions = BASE_STATUS_TRANSITIONS[candidate.status];

    // Handle dynamic transitions
    if (candidate.status === JobCandidateStatus.Interview) {
      availableTransitions = candidate.meeting
        ? {
            back: null,
            forward: null,
            reject: JobCandidateStatus.Rejected,
            withdraw: JobCandidateStatus.Withdrawn
          }
        : {
            back: JobCandidateStatus.Shortlist,
            forward: null,
            reject: JobCandidateStatus.Rejected,
            withdraw: JobCandidateStatus.Withdrawn
          };
    } else if (candidate.status === JobCandidateStatus.Rejected) {
      availableTransitions = {
        back: previousStatus || JobCandidateStatus.ResumeAnalyzed,
        forward:
          previousStatus === JobCandidateStatus.InterviewCompleted ? JobCandidateStatus.Pass : null,
        reject: null,
        withdraw: null
      };
    } else if (candidate.status === JobCandidateStatus.Withdrawn) {
      availableTransitions = {
        back: previousStatus || JobCandidateStatus.ResumeAnalyzed,
        forward:
          previousStatus === JobCandidateStatus.InterviewCompleted ? JobCandidateStatus.Pass : null,
        reject: null,
        withdraw: null
      };
    }

    if (!availableTransitions) {
      availableTransitions = {
        back: null,
        forward: null,
        reject: JobCandidateStatus.Rejected,
        withdraw: JobCandidateStatus.Withdrawn
      };
    }

    // Map transitions to decision list items in order: reject, withdraw, back, forward
    return ACTION_ORDER.map((action) => ({
      action,
      status: availableTransitions[action]
    }))
      .filter(
        (
          item
        ): item is {
          action: 'reject' | 'withdraw' | 'back' | 'forward';
          status: JobCandidateStatus;
        } => item.status !== null
      )
      .map(({ action, status }) => ({
        value: status,
        label: getJobCandidateStatusLabel(status),
        enabled: true,
        onClick: () => updateCandidateStatus(status),
        ...ACTION_COLORS[action]
      }));
  }, [candidate, updateCandidateStatus]);

  const updateFollowUpItem = useCallback(
    async (index: number, item: AlertsAndFollowUpQuestion | null): Promise<boolean> => {
      if (!candidate || !jobId || !candidateId) return false;

      const updatedItems = [...candidate.alerts_and_follow_up_questions];

      if (item === null) {
        const removedItem = updatedItems[index];
        updatedItems.splice(index, 1);

        if (removedItem.title === '') {
          updateCandidateField({
            alerts_and_follow_up_questions: updatedItems
          });
          return true;
        }
      } else {
        updatedItems[index] = item;
      }

      try {
        const response = await fetchAPI<void>({
          request: () =>
            apiUpdateJobCandidateFollowupQuestions({
              jobId,
              candidateId,
              payload: {
                followup_questions: updatedItems
              }
            })
        });

        if (response.success) {
          updateCandidateField({
            alerts_and_follow_up_questions: updatedItems
          });
        } else {
          console.error('Failed to update follow-up questions:', response.message);
        }

        return response.success;
      } catch (error) {
        console.error('An error occurred while updating follow-up questions:', error);
        throw error;
      }
    },
    [candidate, jobId, candidateId, updateCandidateField]
  );

  const handleNavigateToFollowUp = useCallback((tab: CandidateDetailsTab) => {
    switch (tab) {
      case CandidateDetailsTab.Interview:
        setInterviewAnalysisSection(InterviewAnalysisSection.Summary);
        setTab(CandidateDetailsTab.Interview);
        setTimeout(() => {
          interviewAnalysisRef.current?.scrollToFollowUpItems();
        }, 100);
        break;
      case CandidateDetailsTab.Resume:
        setTab(CandidateDetailsTab.Resume);
        setTimeout(() => {
          resumeAnalysisRef.current?.scrollToFollowUpItems();
        }, 100);
        break;
      default:
        break;
    }
  }, []);

  const handleAddInterview = useCallback(() => {
    fetchDetails();
  }, [fetchDetails]);

  const handleEditInterview = useCallback(
    (updatedMeeting: MeetingSummary) => {
      if (!candidate || !candidate.meeting) return;

      const updatedCandidate = {
        ...candidate,
        meeting: {
          ...candidate.meeting,
          title: updatedMeeting.title,
          meeting_start_time: updatedMeeting.meeting_start_time,
          meeting_end_time: updatedMeeting.meeting_end_time,
          meeting_url: updatedMeeting.meeting_url
        }
      };

      setCandidate(updatedCandidate);
    },
    [candidate]
  );

  const handleEditScreening = useCallback(
    (updatedScreening: Screening) => {
      if (!candidate || !candidate.screening) return;

      const updatedCandidate = {
        ...candidate,
        screening: {
          ...candidate.screening,
          session_name: updatedScreening.session_name,
          interview_title: updatedScreening.interview_title,
          interview_description: updatedScreening.interview_description,
          retake_limit: updatedScreening.retake_limit,
          session_time_limit: updatedScreening.session_time_limit,
          questions: updatedScreening.questions,
          deadline: updatedScreening.deadline
        }
      };

      setCandidate(updatedCandidate);
    },
    [candidate]
  );

  const handleNavigateToNewCandidate = useCallback(() => {
    setCandidate(null);
    setPreviousCandidate(null);
    setNextCandidate(null);
    setTooltipState({ previous: false, next: false });
    setTab(CandidateDetailsTab.Summary);
    setInterviewAnalysisSection(InterviewAnalysisSection.Summary);
  }, []);

  const handleNavigateToPreviousCandidate = useCallback(() => {
    if (!previousCandidate) return;
    navigate(`/job/details/${jobId}/candidate/${previousCandidate.candidate_id}`);
    handleNavigateToNewCandidate();
  }, [previousCandidate, jobId, navigate, handleNavigateToNewCandidate]);

  const handleNavigateToNextCandidate = useCallback(() => {
    if (!nextCandidate) return;
    navigate(`/job/details/${jobId}/candidate/${nextCandidate.candidate_id}`);
    handleNavigateToNewCandidate();
  }, [nextCandidate, jobId, navigate, handleNavigateToNewCandidate]);

  const showInterviewTab = useCallback((): boolean => {
    if (!candidate) return false;
    return candidate.progress.includes(JobCandidateStatus.ResumeAnalyzed);
  }, [candidate]);

  const handleAddCandidateToJob = useCallback(
    async (jobIdToAdd: string) => {
      if (!candidate) return;

      try {
        const response = await fetchAPI({
          request: () =>
            apiAddCandidateToJob({
              jobId: jobIdToAdd,
              payload: { candidate_id: candidate.candidate_id }
            })
        });

        if (response.success) {
          // Optionally show a success message or navigate to the new job
          console.log('Candidate added to job successfully');
        } else {
          console.error('Failed to add candidate to job:', response.message);
        }
      } catch (error) {
        console.error('Failed to add candidate to job:', error);
      }
    },
    [candidate]
  );

  if (isLoading) {
    return (
      <Flex w="100%" flexDir="column" p="40px 48px">
        <Flex justifyContent="space-between" alignItems="center" mb="12px">
          <SkeletonText noOfLines={1} w="50%" />
          <Stack direction="row">
            <Skeleton borderRadius="8px" h="40px" w="120px" />
            <Skeleton borderRadius="8px" h="40px" w="120px" />
          </Stack>
        </Flex>
        <Flex justifyContent="space-between" mb="20px">
          <Box
            bg="background.secondary"
            border="1px solid"
            borderColor="neutrals.200"
            borderRadius="8px"
            flex="3"
            h="205px"
          >
            <Flex flexDir="row" alignItems="center" h="100%" p="32px" gap="32px">
              <Stack direction="column" alignItems="center" justifyContent="center">
                <SkeletonCircle size="105px" />
                <Skeleton borderRadius="8px" h="40px" w="80px" />
              </Stack>
              <SkeletonText noOfLines={3} gap="4" />
            </Flex>
          </Box>
          <Box w="16px" />
          <Box
            bg="background.secondary"
            border="1px solid"
            borderColor="neutrals.200"
            borderRadius="8px"
            flex="2"
            p="32px"
          >
            <SkeletonText noOfLines={3} gap="4" />
          </Box>
        </Flex>
        <Box
          bg="background.secondary"
          border="1px solid"
          borderColor="neutrals.200"
          borderRadius="16px"
          flex="1"
          h="120px"
          p="32px"
        >
          <SkeletonText noOfLines={10} gap="4" />
        </Box>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex w="100%" flexDir="column" p="40px 48px" justifyContent="center" alignItems="center">
        <Text color="red.500" fontSize="18px" mb="16px">
          {error}
        </Text>
        <Button
          variant="outline"
          color="primary.900"
          fontSize="16px"
          borderRadius="8px"
          px="44px"
          py="12px"
          border="1px solid"
          borderColor="primary.900"
          onClick={() => fetchDetails()}
        >
          Retry
        </Button>
      </Flex>
    );
  }

  if (!candidate || !job) {
    return null;
  }

  return (
    <Flex w="100%" flexDir="column" p="40px 48px">
      <Flex justifyContent="space-between" alignItems="center" mb="12px">
        <BreadcrumbNavigation
          job={job}
          candidateName={getCandidateFullName(candidate.first_name, candidate.last_name)}
          candidateId={candidateId!}
          onNavigate={navigate}
        />
        <Flex gap="16px" alignItems="center">
          {previousCandidate && nextCandidate && (
            <Box borderRadius="full" bg="neutrals.0" boxShadow="md">
              <Flex justifyContent="space-between">
                <Box
                  cursor="pointer"
                  onMouseEnter={() => setTooltipState((prev) => ({ ...prev, previous: true }))}
                  onMouseLeave={() => setTooltipState((prev) => ({ ...prev, previous: false }))}
                >
                  <Tooltip
                    content={<CandidateCard candidate={previousCandidate} />}
                    open={tooltipState.previous}
                    contentProps={{
                      bg: 'neutrals.0',
                      color: 'neutrals.900',
                      borderRadius: '12px',
                      border: '1px solid',
                      borderColor: 'neutrals.200',
                      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                      p: '8px',
                      fontSize: '14px'
                    }}
                  >
                    <Button
                      variant="ghost"
                      fontSize="16px"
                      color="neutrals.800"
                      borderRadius="full"
                      _hover={{ bg: 'transparent', color: 'primary.900' }}
                      onClick={handleNavigateToPreviousCandidate}
                      disabled={!previousCandidate}
                    >
                      <CaretLeft /> Previous
                    </Button>
                  </Tooltip>
                </Box>

                <Box
                  cursor="pointer"
                  onMouseEnter={() => setTooltipState((prev) => ({ ...prev, next: true }))}
                  onMouseLeave={() => setTooltipState((prev) => ({ ...prev, next: false }))}
                >
                  <Tooltip
                    content={<CandidateCard candidate={nextCandidate} />}
                    open={tooltipState.next}
                    contentProps={{
                      bg: 'neutrals.0',
                      color: 'neutrals.900',
                      borderRadius: '12px',
                      border: '1px solid',
                      borderColor: 'neutrals.200',
                      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                      p: '8px',
                      fontSize: '14px'
                    }}
                  >
                    <Button
                      variant="ghost"
                      fontSize="16px"
                      color="neutrals.800"
                      borderRadius="full"
                      _hover={{ bg: 'transparent', color: 'primary.900' }}
                      onClick={handleNavigateToNextCandidate}
                      disabled={!nextCandidate}
                    >
                      Next <CaretRight />
                    </Button>
                  </Tooltip>
                </Box>
              </Flex>
            </Box>
          )}
        </Flex>
      </Flex>
      <Flex justifyContent="space-between" mb="20px">
        <Box
          bg="background.secondary"
          border="1px solid"
          borderColor="neutrals.200"
          borderRadius="12px"
          flex="3"
          h="205px"
          position="relative"
        >
          <Box position="absolute" top="16px" right="16px" zIndex="1">
            <EditCandidateDialog
              candidateId={candidate.candidate_id}
              firstName={candidate.first_name}
              lastName={candidate.last_name}
              email={candidate.email}
              phone={candidate.phone}
              linkedin={candidate.linkedin}
              onUpdateCandidate={(first_name, last_name, email, phone, linkedin) =>
                updateCandidateField({ first_name, last_name, email, phone, linkedin })
              }
              iconOnly
            />
          </Box>
          <Flex flexDir="row" alignItems="center" h="100%" p="32px" gap="32px">
            <Stack direction="column" alignItems="center" justifyContent="center">
              <Box h="105px" w="105px">
                <CircleProgressBar value={candidate.overall_score} candidate={candidate} />
              </Box>
              <ScoreDetailsDialog
                candidate={candidate}
                jobId={jobId!}
                onUpdateScore={(userScore) =>
                  updateCandidateField({
                    user_score: userScore,
                    overall_score: userScore.overall_score || userScore.resume_score || 0
                  })
                }
              />
            </Stack>
            <Stack direction="column" justifyContent="center" gap="8px">
              <Flex alignItems="center" gap="12px">
                <Box fontSize="32px" fontWeight="bold">
                  {getCandidateFullName(candidate.first_name, candidate.last_name)}
                </Box>
                <Box
                  bg="transparent"
                  borderRadius="8px"
                  w="32px"
                  h="32px"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                  onClick={() => navigate(`/candidate/${candidate.candidate_id}`)}
                  color="primary.900"
                >
                  <UserCircle size={24} />
                </Box>
              </Flex>
              <Box fontSize="16px" color="neutrals.800" display="flex" alignItems="center">
                {lastWorkExperience
                  ? `${lastWorkExperience.job_role} @ ${lastWorkExperience.company_name}`
                  : 'No work experience'}
              </Box>
              <Stack direction="row" gap="12px">
                {candidate.linkedin && (
                  <Box
                    bg="background.secondary"
                    borderRadius="8px"
                    w="32px"
                    h="32px"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    _hover={{ cursor: 'pointer' }}
                  >
                    <a
                      href={
                        candidate.linkedin.startsWith('http')
                          ? candidate.linkedin
                          : `https://${candidate.linkedin}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <LinkedinLogo size={32} color="#1275B1" weight="fill" />
                    </a>
                  </Box>
                )}

                <Box
                  bg="primary.100"
                  borderRadius="8px"
                  w="32px"
                  h="32px"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  _hover={{ cursor: 'pointer' }}
                  onClick={() => window.open(`mailto:${candidate.email}`)}
                >
                  <Envelope size={24} color="#3C5CC6" />
                </Box>
              </Stack>
            </Stack>
          </Flex>
        </Box>
        <Box w="16px" />
        <Box
          bg="background.secondary"
          border="1px solid"
          borderColor="neutrals.200"
          borderRadius="12px"
          flex="2"
          position="relative"
        >
          <Flex p="32px" justify="space-between" alignItems="center">
            <Text fontSize="18px" color="neutrals.700" fontWeight="semibold" mb="8px">
              Status
            </Text>
            <Flex gap="8px">
              {getDecisionList.map((item) => (
                <Button
                  key={item.value}
                  size="sm"
                  p="0px 6px"
                  bg={item.bgColor}
                  borderRadius="full"
                  color={item.color}
                  fontSize="13px"
                  disabled={
                    [JobCandidateStatus.New].includes(candidate.status) ||
                    isStatusUpdating ||
                    !item.enabled
                  }
                  loading={isStatusUpdating}
                  onClick={item.onClick}
                  _hover={{ bg: item.hoverBgColor, color: 'neutrals.0' }}
                  boxShadow="xs"
                >
                  {item.label}
                </Button>
              ))}
            </Flex>
          </Flex>
          <Flex justifyContent="flex-start" alignItems="flex-start" h="100%" p="12px 32px">
            <Step steps={statusList} currentStep={statusIndex} />
          </Flex>
        </Box>
      </Flex>
      <Box
        bg="background.secondary"
        border="1px solid"
        borderColor="neutrals.200"
        borderRadius="16px"
        flex="1"
        h="120px"
      >
        {candidate.status === JobCandidateStatus.New ? (
          <Flex
            w="100%"
            mt="120px"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
          >
            <Spinner size="lg" color="primary.900" mb="16px" />
            <Box color="neutrals.900" fontSize="18px" fontWeight="semibold" textAlign="center">
              Analysis in progress, please wait a moment.
            </Box>
          </Flex>
        ) : (
          <Flex flexDir="column" justifyContent="top" alignItems="left" h="100%" p="32px">
            <Tabs.Root
              value={tab}
              onValueChange={(e) => setTab(e.value as CandidateDetailsTab)}
              colorPalette="blue"
            >
              <Flex justifyContent="space-between" alignItems="center" mb="8px">
                <Tabs.List>
                  <Tabs.Trigger value={CandidateDetailsTab.Summary} asChild>
                    <Box
                      fontSize="18px"
                      color={tab === CandidateDetailsTab.Summary ? 'primary.1000' : 'neutrals.900'}
                    >
                      Summary
                    </Box>
                  </Tabs.Trigger>
                  <Tabs.Trigger value={CandidateDetailsTab.Resume} asChild>
                    <Box
                      fontSize="18px"
                      color={tab === CandidateDetailsTab.Resume ? 'primary.1000' : 'neutrals.900'}
                    >
                      Resume
                    </Box>
                  </Tabs.Trigger>
                  {showInterviewTab() && (
                    <Tabs.Trigger value={CandidateDetailsTab.Interview} asChild>
                      <Box
                        fontSize="18px"
                        color={
                          tab === CandidateDetailsTab.Interview ? 'primary.1000' : 'neutrals.900'
                        }
                      >
                        Interview
                      </Box>
                    </Tabs.Trigger>
                  )}
                  <Tabs.Trigger value={CandidateDetailsTab.Notes} asChild>
                    <Box
                      fontSize="18px"
                      color={tab === CandidateDetailsTab.Notes ? 'primary.1000' : 'neutrals.900'}
                    >
                      Note
                    </Box>
                  </Tabs.Trigger>
                </Tabs.List>
                <AddCandidateToJobDialog
                  candidateId={candidate.candidate_id}
                  candidateName={getCandidateFullName(candidate.first_name, candidate.last_name)}
                  onAddToJob={handleAddCandidateToJob}
                  buttonText="Add to Another Job"
                />
              </Flex>
              <Tabs.Content value={CandidateDetailsTab.Summary}>
                <CandidateSummary
                  applicationQuestionAnswers={candidate.application_answers}
                  overviewAnalysisResult={candidate.overview_analysis_result}
                  resumeAnalysisResult={candidate.resume_analysis_result}
                  followUpQuestions={candidate.alerts_and_follow_up_questions}
                  onNavigateToFollowUp={handleNavigateToFollowUp}
                />
              </Tabs.Content>
              <Tabs.Content value={CandidateDetailsTab.Resume}>
                <ResumeAnalysis
                  ref={resumeAnalysisRef}
                  candidate={candidate}
                  onUpdateItem={updateFollowUpItem}
                />
              </Tabs.Content>
              {showInterviewTab() && (
                <Tabs.Content value={CandidateDetailsTab.Interview}>
                  <InterviewAnalysisIndex
                    ref={interviewAnalysisRef}
                    job={job}
                    candidate={candidate}
                    meeting={candidate.meeting}
                    screening={candidate.screening}
                    analysisResult={
                      candidate.interview_analysis_result ||
                      candidate.screening_analysis_result ||
                      null
                    }
                    followUpQuestions={candidate.alerts_and_follow_up_questions}
                    onUpdateItem={updateFollowUpItem}
                    section={interviewAnalysisSection}
                    setSection={setInterviewAnalysisSection}
                    onAddInterview={handleAddInterview}
                    onEditInterview={handleEditInterview}
                    onEditScreening={handleEditScreening}
                  />
                </Tabs.Content>
              )}
              <Tabs.Content value={CandidateDetailsTab.Notes}>
                <CandidateNotes jobId={jobId!} candidateId={candidateId!} notes={candidate.notes} />
              </Tabs.Content>
            </Tabs.Root>
          </Flex>
        )}
      </Box>
    </Flex>
  );
}
